import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Skeleton
} from '@mui/material';
import { Refresh, AdminPanelSettings, People, Assignment, Edit, Star, TrendingUp, AttachMoney, PersonOff } from '@mui/icons-material';
import { BugReport, Visibility, VisibilityOff } from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { ROLES, getRoleDisplayName } from '../../services/roleService';
import BackButton from '../BackButton';

const AdminDashboardScreen = () => {
  const { currentUser, logout } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [affiliateCodes, setAffiliateCodes] = useState([]);
  const [adminLogs, setAdminLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  const [subscriptionMetrics, setSubscriptionMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState('');
  // Invite-only affiliate program: totals keyed by uid come from the
  // manage-affiliates function (ledger sums the client can't read).
  const [affiliateTotals, setAffiliateTotals] = useState({});
  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [grantTarget, setGrantTarget] = useState(null);
  const [grantCustomCode, setGrantCustomCode] = useState('');
  const [affiliateBusy, setAffiliateBusy] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Load subscription metrics when switching to Subscriptions tab
  useEffect(() => {
    if (activeTab === 3 && !subscriptionMetrics && !metricsLoading) {
      loadSubscriptionMetrics();
    }
  }, [activeTab]);

  const loadSubscriptionMetrics = async () => {
    setMetricsLoading(true);
    setMetricsError('');
    
    try {
      const token = await currentUser.getIdToken();
      
      const functionUrl = process.env.NODE_ENV === 'development'
        ? 'http://localhost:8888/.netlify/functions/stripe-get-subscription-metrics'
        : '/.netlify/functions/stripe-get-subscription-metrics';
      
      const response = await fetch(functionUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load subscription metrics');
      }

      const data = await response.json();
      setSubscriptionMetrics(data);
    } catch (err) {
      console.error('Error loading subscription metrics:', err);
      setMetricsError(err.message || 'Failed to load subscription metrics');
    } finally {
      setMetricsLoading(false);
    }
  };

  const loadDashboardData = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Force token refresh to ensure we have latest custom claims
      console.log('Forcing token refresh...');
      await currentUser.getIdToken(true);
      
      // Verify token claims
      const tokenResult = await currentUser.getIdTokenResult();
      console.log('Current token claims:', tokenResult.claims);
      console.log('User role in token:', tokenResult.claims.role);
      
      // Store debug info
      setDebugInfo({
        email: currentUser.email,
        uid: currentUser.uid,
        emailVerified: currentUser.emailVerified,
        tokenClaims: tokenResult.claims,
        tokenIssuedAt: new Date(tokenResult.issuedAtTime).toLocaleString(),
        tokenExpiresAt: new Date(tokenResult.expirationTime).toLocaleString()
      });
      
      // Check if user has admin role
      if (tokenResult.claims.role !== 'admin') {
        throw new Error(`Access denied. Your account does not have admin privileges. Current role: ${tokenResult.claims.role || 'none'}. Please contact an administrator if you believe this is an error.`);
      }
      
      // Load users with affiliate codes
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = [];
      
      usersSnapshot.forEach((doc) => {
        const userData = doc.data();
        usersData.push({
          uid: doc.id,
          email: userData.email,
          displayName: userData.displayName,
          firstName: userData.firstName,
          lastName: userData.lastName,
          role: userData.role || 'user',
          affiliateCode: userData.affiliateCode,
          isAffiliate: userData.isAffiliate,
          affiliateAccess: userData.affiliateAccess || null,
          referralUsers: userData.referralUsers || [],
          profileCreatedAt: userData.profileCreatedAt,
          roleAssignedAt: userData.roleAssignedAt
        });
      });

      setUsers(usersData);

      // Extract affiliate codes for the codes table — granted affiliates
      // only under the invite-only program (legacy auto-codes are inert).
      const codes = usersData
        .filter(user => user.affiliateAccess?.status === 'granted' && (user.affiliateAccess.code || user.affiliateCode))
        .map(user => ({
          code: user.affiliateAccess.code || user.affiliateCode,
          userId: user.uid,
          userName: user.displayName,
          email: user.email,
          referralCount: user.referralUsers.length
        }));

      setAffiliateCodes(codes);

      // Load per-affiliate earnings totals from the manage-affiliates
      // function. Non-fatal: if the function isn't reachable yet, the
      // Affiliates tab still renders access states without totals.
      try {
        const totalsToken = await currentUser.getIdToken();
        const affiliatesUrl = process.env.NODE_ENV === 'development'
          ? 'http://localhost:8888/.netlify/functions/manage-affiliates'
          : '/.netlify/functions/manage-affiliates';
        const affiliatesResponse = await fetch(affiliatesUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${totalsToken}`
          },
          body: JSON.stringify({ action: 'listAffiliates' })
        });
        if (affiliatesResponse.ok) {
          const affiliatesData = await affiliatesResponse.json();
          const totalsMap = {};
          (affiliatesData.affiliates || []).forEach((affiliate) => {
            totalsMap[affiliate.uid] = affiliate;
          });
          setAffiliateTotals(totalsMap);
        } else {
          console.warn('listAffiliates returned', affiliatesResponse.status);
        }
      } catch (totalsError) {
        console.warn('Could not load affiliate totals:', totalsError);
      }

      // Load admin logs
      console.log('Attempting to load admin logs...');
      const logsSnapshot = await getDocs(collection(db, 'adminLogs'));
      const logsData = [];
      
      logsSnapshot.forEach((doc) => {
        logsData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      // Sort logs by timestamp (newest first)
      logsData.sort((a, b) => {
        const aTime = a.timestamp?.toDate?.() || new Date(0);
        const bTime = b.timestamp?.toDate?.() || new Date(0);
        return bTime - aTime;
      });
      
      setAdminLogs(logsData);
      console.log('Successfully loaded admin logs:', logsData.length);
      
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      console.error('Error code:', err.code);
      console.error('Error details:', err);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to load dashboard data: ' + err.message;
      
      if (err.code === 'permission-denied') {
        errorMessage = 'Permission denied. This could happen if:\n' +
                      '• Your admin role was recently assigned and your browser needs a fresh login\n' +
                      '• Your custom claims need to be refreshed\n' +
                      '\nTo fix this: Sign out completely, clear your browser cache, and sign back in.\n\n' +
                      'If the problem persists, contact a system administrator.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      setError('');
      setSuccess('');
      
      const token = await currentUser.getIdToken();
      
      // Use absolute URL for local dev, relative for production
      const functionUrl = process.env.NODE_ENV === 'development'
        ? 'http://localhost:8888/.netlify/functions/manage-roles'
        : '/.netlify/functions/manage-roles';
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'updateRole',
          targetUserId: userId,
          newRole
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update role');
      }

      setSuccess(`Successfully updated user role to ${newRole}`);
      
      // Reload dashboard data
      await loadDashboardData();
    } catch (err) {
      console.error('Error updating role:', err);
      setError('Failed to update role: ' + err.message);
    }
  };

  // === Invite-only affiliate program: grant / revoke ===
  const callManageAffiliates = async (payload) => {
    const token = await currentUser.getIdToken();

    const functionUrl = process.env.NODE_ENV === 'development'
      ? 'http://localhost:8888/.netlify/functions/manage-affiliates'
      : '/.netlify/functions/manage-affiliates';

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Affiliate action failed');
    }
    return data;
  };

  const handleOpenGrantDialog = (user) => {
    setGrantTarget(user);
    setGrantCustomCode(user.affiliateAccess?.code || '');
    setGrantDialogOpen(true);
  };

  const handleCloseGrantDialog = () => {
    setGrantDialogOpen(false);
    setGrantTarget(null);
    setGrantCustomCode('');
  };

  const handleGrantAffiliate = async () => {
    if (!grantTarget) return;
    try {
      setError('');
      setSuccess('');
      setAffiliateBusy(true);

      const payload = { action: 'grantAffiliate', targetUserId: grantTarget.uid };
      const trimmedCode = grantCustomCode.trim();
      // Only send a custom code when the admin typed one that differs
      // from what a re-grant would reuse anyway.
      if (trimmedCode && trimmedCode.toUpperCase() !== (grantTarget.affiliateAccess?.code || '').toUpperCase()) {
        payload.customCode = trimmedCode;
      }

      const data = await callManageAffiliates(payload);
      setSuccess(`Affiliate access granted to ${grantTarget.displayName || grantTarget.email} with code ${data.affiliateCode}`);
      handleCloseGrantDialog();
      await loadDashboardData();
    } catch (err) {
      console.error('Error granting affiliate access:', err);
      setError('Failed to grant affiliate access: ' + err.message);
    } finally {
      setAffiliateBusy(false);
    }
  };

  const handleRevokeAffiliate = async (user) => {
    const confirmed = window.confirm(
      `Revoke affiliate access for ${user.displayName || user.email}? Their code stops working and earnings stop accruing immediately.`
    );
    if (!confirmed) return;

    try {
      setError('');
      setSuccess('');
      setAffiliateBusy(true);

      await callManageAffiliates({ action: 'revokeAffiliate', targetUserId: user.uid });
      setSuccess(`Affiliate access revoked for ${user.displayName || user.email}`);
      await loadDashboardData();
    } catch (err) {
      console.error('Error revoking affiliate access:', err);
      setError('Failed to revoke affiliate access: ' + err.message);
    } finally {
      setAffiliateBusy(false);
    }
  };

  const getAffiliateStatus = (user) => {
    if (user.affiliateAccess?.status === 'granted') return 'granted';
    if (user.affiliateAccess?.status === 'revoked') return 'revoked';
    if (user.affiliateCode) return 'legacy';
    return 'none';
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || ''
    });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingUser(null);
    setEditForm({
      firstName: '',
      lastName: '',
      email: ''
    });
  };

  const handleSaveUserEdit = async () => {
    try {
      setError('');
      setSuccess('');

      const token = await currentUser.getIdToken();
      
      // Use absolute URL for local dev, relative for production
      const functionUrl = process.env.NODE_ENV === 'development'
        ? 'http://localhost:8888/.netlify/functions/manage-roles'
        : '/.netlify/functions/manage-roles';
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'updateUserDetails',
          targetUserId: editingUser.uid,
          updates: {
            firstName: editForm.firstName,
            lastName: editForm.lastName,
            email: editForm.email,
            displayName: `${editForm.firstName} ${editForm.lastName}`
          }
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update user');
      }

      setSuccess('Successfully updated user details');
      handleCloseEditDialog();
      
      // Reload dashboard data
      await loadDashboardData();
    } catch (err) {
      console.error('Error updating user:', err);
      setError('Failed to update user: ' + err.message);
    }
  };

  const handleForceReauth = async () => {
    try {
      setSuccess('Logging out to refresh authentication...');
      await logout();
      // The user will be redirected to login page automatically
    } catch (err) {
      console.error('Error during logout:', err);
      setError('Failed to logout: ' + err.message);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case ROLES.ADMIN:
        return 'error';
      case ROLES.HANDICAPPER:
        return 'primary';
      case ROLES.USER:
        return 'default';
      default:
        return 'default';
    }
  };

  const filteredUsers = roleFilter === 'all' 
    ? users 
    : users.filter(user => user.role === roleFilter);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading dashboard...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <BackButton />
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <AdminPanelSettings sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
        <Typography variant="h4" component="h1">
          Admin Dashboard
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Show Debug Info">
          <IconButton 
            onClick={() => setShowDebugInfo(!showDebugInfo)} 
            color={showDebugInfo ? "secondary" : "default"}
          >
            {showDebugInfo ? <VisibilityOff /> : <BugReport />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Refresh Data">
          <IconButton onClick={loadDashboardData} color="primary">
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }} 
          onClose={() => setError('')}
          action={
            error.includes('Permission denied') ? (
              <Button 
                color="inherit" 
                size="small" 
                onClick={handleForceReauth}
                variant="outlined"
                sx={{ ml: 2 }}
              >
                Force Re-login
              </Button>
            ) : null
          }
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Debug Information Panel */}
      {showDebugInfo && debugInfo && (
        <Card sx={{ mb: 3, bgcolor: 'background.default' }}>
          <CardContent>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <BugReport sx={{ mr: 1 }} />
              Debug Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2"><strong>Email:</strong> {debugInfo.email}</Typography>
                <Typography variant="body2"><strong>UID:</strong> {debugInfo.uid}</Typography>
                <Typography variant="body2"><strong>Email Verified:</strong> {debugInfo.emailVerified ? '✅ Yes' : '❌ No'}</Typography>
                <Typography variant="body2"><strong>Token Role:</strong> {debugInfo.tokenClaims?.role || 'None'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2"><strong>Token Issued:</strong> {debugInfo.tokenIssuedAt}</Typography>
                <Typography variant="body2"><strong>Token Expires:</strong> {debugInfo.tokenExpiresAt}</Typography>
                <Typography variant="body2"><strong>All Claims:</strong></Typography>
                <Box component="pre" sx={{ fontSize: '0.75rem', bgcolor: 'grey.100', p: 1, borderRadius: 1, mt: 1 }}>
                  {JSON.stringify(debugInfo.tokenClaims, null, 2)}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Stats Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <People sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h4">{users.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Users
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Assignment sx={{ fontSize: 40, mr: 2, color: 'secondary.main' }} />
                <Box>
                  <Typography variant="h4">
                    {users.filter(u => u.role === ROLES.HANDICAPPER).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Handicappers
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AdminPanelSettings sx={{ fontSize: 40, mr: 2, color: 'error.main' }} />
                <Box>
                  <Typography variant="h4">
                    {affiliateCodes.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Affiliate Codes
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Affiliates" />
          <Tab label="User Management" />
          <Tab label="Admin Logs" />
          <Tab label="Subscriptions" icon={<Star fontSize="small" />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      {activeTab === 0 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            The affiliate program is invite-only. Grant access to a user to mint their
            referral code (10% revenue share of referred subscribers' payments, for as
            long as they stay subscribed). Revoking stops the code and earnings
            immediately. Users marked <strong>Legacy</strong> hold a pre-lockdown
            auto-generated code that no longer works at signup.
          </Alert>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>User</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Code</strong></TableCell>
                  <TableCell><strong>Access</strong></TableCell>
                  <TableCell><strong>Referrals</strong></TableCell>
                  <TableCell><strong>Earnings (month / lifetime)</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => {
                  const status = getAffiliateStatus(user);
                  const totals = affiliateTotals[user.uid];
                  const code = user.affiliateAccess?.code || user.affiliateCode;
                  return (
                    <TableRow key={user.uid}>
                      <TableCell>{user.displayName}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        {code
                          ? <Chip label={code} color={status === 'granted' ? 'primary' : 'default'} variant="outlined" size="small" />
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {status === 'granted' && <Chip label="Granted" size="small" color="success" />}
                        {status === 'revoked' && <Chip label="Revoked" size="small" color="error" />}
                        {status === 'legacy' && <Chip label="Legacy (inactive)" size="small" variant="outlined" />}
                        {status === 'none' && <Chip label="None" size="small" variant="outlined" />}
                      </TableCell>
                      <TableCell>
                        <Chip label={user.referralUsers.length} size="small" />
                        {totals ? (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {totals.activeSubscribers} subscribed
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {totals
                          ? `$${(totals.monthShareCents / 100).toFixed(2)} / $${(totals.lifetimeShareCents / 100).toFixed(2)}`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {status === 'granted' ? (
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            disabled={affiliateBusy}
                            onClick={() => handleRevokeAffiliate(user)}
                          >
                            Revoke
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            color="success"
                            variant="contained"
                            disabled={affiliateBusy}
                            onClick={() => handleOpenGrantDialog(user)}
                          >
                            Grant
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {activeTab === 1 && (
        <>
          <Box sx={{ mb: 2 }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Filter by Role</InputLabel>
              <Select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                label="Filter by Role"
              >
                <MenuItem value="all">All Users</MenuItem>
                <MenuItem value={ROLES.ADMIN}>Admins</MenuItem>
                <MenuItem value={ROLES.HANDICAPPER}>Handicappers</MenuItem>
                <MenuItem value={ROLES.USER}>Users</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Current Role</strong></TableCell>
                  <TableCell><strong>Change Role</strong></TableCell>
                  <TableCell><strong>Affiliate Code</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.uid}>
                    <TableCell>{user.displayName}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip 
                        label={getRoleDisplayName(user.role)} 
                        color={getRoleColor(user.role)} 
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select
                          value={user.role}
                          onChange={(e) => handleRoleChange(user.uid, e.target.value)}
                          disabled={user.uid === currentUser.uid} // Can't change own role
                        >
                          <MenuItem value={ROLES.USER}>User</MenuItem>
                          <MenuItem value={ROLES.HANDICAPPER}>Handicapper</MenuItem>
                          <MenuItem value={ROLES.ADMIN}>Admin</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      {user.affiliateCode && (
                        <Chip label={user.affiliateCode} size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit User Details">
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleEditUser(user)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {activeTab === 2 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Action</strong></TableCell>
                <TableCell><strong>Details</strong></TableCell>
                <TableCell><strong>Performed By</strong></TableCell>
                <TableCell><strong>Timestamp</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {adminLogs.slice(0, 50).map((log) => (
                <TableRow key={log.id}>
                  <TableCell>
                    <Chip label={log.action} size="small" color="primary" />
                  </TableCell>
                  <TableCell>
                    {log.details?.message || log.details?.oldRole 
                      ? `${log.details?.oldRole} → ${log.details?.newRole}` 
                      : 'N/A'}
                  </TableCell>
                  <TableCell>{log.performedBy || 'System'}</TableCell>
                  <TableCell>
                    {log.timestamp?.toDate?.()?.toLocaleString() || 'N/A'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Subscriptions Tab */}
      {activeTab === 3 && (
        <Box>
          {metricsLoading && (
            <Grid container spacing={3}>
              {[1, 2, 3, 4].map(i => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Card>
                    <CardContent>
                      <Skeleton variant="text" width="60%" height={30} />
                      <Skeleton variant="text" width="40%" height={50} />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Skeleton variant="rectangular" height={300} />
              </Grid>
            </Grid>
          )}

          {metricsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {metricsError}
              <Button size="small" onClick={loadSubscriptionMetrics} sx={{ ml: 2 }}>
                Retry
              </Button>
            </Alert>
          )}

          {subscriptionMetrics && !metricsLoading && (
            <>
              {/* Subscription Stats Cards */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: 'success.light' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Star sx={{ fontSize: 40, mr: 2, color: 'success.dark' }} />
                        <Box>
                          <Typography variant="h4" color="success.dark">
                            {subscriptionMetrics.summary.activeSubscribers}
                          </Typography>
                          <Typography variant="body2" color="success.dark">
                            Active Subscribers
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: 'primary.light' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AttachMoney sx={{ fontSize: 40, mr: 2, color: 'primary.dark' }} />
                        <Box>
                          <Typography variant="h4" color="primary.dark">
                            ${subscriptionMetrics.summary.monthlyRecurringRevenue}
                          </Typography>
                          <Typography variant="body2" color="primary.dark">
                            Monthly Revenue
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: 'warning.light' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TrendingUp sx={{ fontSize: 40, mr: 2, color: 'warning.dark' }} />
                        <Box>
                          <Typography variant="h4" color="warning.dark">
                            {subscriptionMetrics.summary.trialingUsers}
                          </Typography>
                          <Typography variant="body2" color="warning.dark">
                            On Trial
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: 'error.light' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonOff sx={{ fontSize: 40, mr: 2, color: 'error.dark' }} />
                        <Box>
                          <Typography variant="h4" color="error.dark">
                            {subscriptionMetrics.summary.churnRate}%
                          </Typography>
                          <Typography variant="body2" color="error.dark">
                            Churn Rate
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* 30-Day Summary */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Last 30 Days Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">New Subscriptions</Typography>
                    <Typography variant="h5">{subscriptionMetrics.last30Days.newSubscriptions}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">Trial Starts</Typography>
                    <Typography variant="h5">{subscriptionMetrics.last30Days.trialStarts}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">Cancellations</Typography>
                    <Typography variant="h5">{subscriptionMetrics.last30Days.cancellations}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">Revenue</Typography>
                    <Typography variant="h5">${subscriptionMetrics.last30Days.revenue}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Revenue Chart */}
              {subscriptionMetrics.dailyStats.length > 0 && (
                <Paper sx={{ p: 2, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Daily Activity (Last 30 Days)
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={subscriptionMetrics.dailyStats}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        />
                        <YAxis />
                        <RechartsTooltip />
                        <Legend />
                        <Line type="monotone" dataKey="newSubscriptions" stroke="#4caf50" name="New Subs" />
                        <Line type="monotone" dataKey="trialStarts" stroke="#ff9800" name="Trials" />
                        <Line type="monotone" dataKey="cancellations" stroke="#f44336" name="Cancellations" />
                        <Line type="monotone" dataKey="revenue" stroke="#2196f3" name="Revenue ($)" />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              )}

              {/* Recent Events Table */}
              <Paper>
                <Typography variant="h6" sx={{ p: 2, pb: 0 }}>
                  Recent Subscription Events
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Event</strong></TableCell>
                        <TableCell><strong>User</strong></TableCell>
                        <TableCell><strong>Amount</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Time</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {subscriptionMetrics.recentEvents.map((event) => (
                        <TableRow key={event.id}>
                          <TableCell>
                            <Chip 
                              label={event.eventType.replace(/_/g, ' ')} 
                              size="small" 
                              color={
                                event.eventType.includes('created') || event.eventType.includes('renewed') ? 'success' :
                                event.eventType.includes('cancelled') || event.eventType.includes('failed') ? 'error' :
                                event.eventType.includes('trial') ? 'warning' : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell>{event.userEmail || 'N/A'}</TableCell>
                          <TableCell>{event.amount ? `$${event.amount}` : '-'}</TableCell>
                          <TableCell>
                            <Chip label={event.status || 'N/A'} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            {new Date(event.timestamp).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                      {subscriptionMetrics.recentEvents.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            No subscription events yet
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {/* Refresh Button */}
              <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Button 
                  startIcon={<Refresh />} 
                  onClick={loadSubscriptionMetrics}
                  disabled={metricsLoading}
                >
                  Refresh Metrics
                </Button>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Grant Affiliate Dialog */}
      <Dialog open={grantDialogOpen} onClose={handleCloseGrantDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Grant Affiliate Access</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2">
              Granting <strong>{grantTarget?.displayName || grantTarget?.email}</strong> affiliate
              access mints their referral code. They earn 10% of every payment made by
              people who sign up with it, for as long as those people stay subscribed.
            </Typography>
            <TextField
              label="Custom code (optional)"
              fullWidth
              value={grantCustomCode}
              onChange={(e) => setGrantCustomCode(e.target.value)}
              helperText="Leave blank to auto-generate (or reuse their previous code). 3-20 characters: letters, numbers, dash, underscore."
            />
            {grantTarget && (
              <Alert severity="info">
                Their link will be: bettoradvantage.com/r/{(grantCustomCode.trim().toUpperCase() || grantTarget.affiliateAccess?.code || '…')}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGrantDialog} disabled={affiliateBusy}>Cancel</Button>
          <Button
            onClick={handleGrantAffiliate}
            variant="contained"
            color="success"
            disabled={affiliateBusy}
          >
            Grant Access
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User Details</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="First Name"
              fullWidth
              value={editForm.firstName}
              onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
              required
            />
            <TextField
              label="Last Name"
              fullWidth
              value={editForm.lastName}
              onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
              required
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={editForm.email}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              required
            />
            <Alert severity="info">
              Changing the email will update the user's Firestore record, but will NOT change their Firebase Authentication email. The user will still log in with their original email.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveUserEdit} 
            variant="contained" 
            color="primary"
            disabled={!editForm.firstName || !editForm.lastName || !editForm.email}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminDashboardScreen;
